import { useEffect, useRef, useState, type FormEvent } from 'react';
import {CitationCredit} from './SoftwareAcknowledgements';
import { BookOpen, ClipboardList, GraduationCap, Home, ShieldCheck, Users } from 'lucide-react';
import { api } from './api';
import { ErrorNotice, Spinner } from './components';
import type { User } from './AccountJourney';
import './educator.css';
import './portal-design.css';
import { usePortalAppearance } from './portal-appearance';

type Role = 'administrator' | 'leader' | 'educator' | 'reviewer' | 'student';
type Member = {
  id: string;
  name?: string;
  role: Role;
  active: boolean;
  revision: number;
  courseIds: string[];
};
type Course = {
  id: string;
  title: string;
  module: string;
  year: string;
  archived: boolean;
  revision: number;
};
type Brief = {
  courseId: string;
  title: string;
  instructions: string;
  outcomes: string;
  deliverables: string;
  referencing: string;
  assistance: string;
  dueAt: string;
  timezone: string;
};
type Assignment = {
  id: string;
  courseId: string;
  revision: number;
  draft: Brief;
  published: (Brief & { number: number; withdrawn: boolean }) | null;
  publications: (Brief & { number: number; withdrawn: boolean; at: string })[];
};
type Feedback = {
  id: string;
  author: string;
  text: string;
  released: boolean;
  revision: number;
  at: string;
};
type Share = {
  id: string;
  title: string;
  version: number;
  courseId: string;
  capturedAt: string;
  expiresAt: string;
  active: boolean;
  feedback: Feedback[];
};
type Invitation = {
  id: string;
  orgId: string;
  orgName: string;
  email?: string;
  role: Role;
  expiresAt: string;
  status?: string;
  revision: number;
};
type HomeData = {
  organisations: { id: string; name: string; role: Role }[];
  invitations: Invitation[];
};
type Data = {
  organisation: { id: string; name: string; institutionId: string };
  membership: Member;
  courses: Course[];
  assignments: Assignment[];
  shares: Share[];
  members: Member[];
  invitations: Invitation[];
  audit: { actor: string; action: string; target: string; at: string }[];
};
type Review = {
  share: Share;
  snapshot: {
    title: string;
    version: number;
    limitations: string;
    files: { id: string; name: string; text: string; version: number }[];
    sources: { title: string; url: string }[];
  };
  feedback: Feedback[];
};
type Command = { action: string; requestId?: string; [key: string]: unknown };
type Pending = { org: string; body: Command };
const tabs = [
  ['Home', Home],
  ['Courses', BookOpen],
  ['Assignments', ClipboardList],
  ['Reviews', GraduationCap],
  ['Policies', ShieldCheck],
  ['Administration', Users],
] as const;
const date = (value: string) =>
  new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

function saved<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
}
function remember(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

export function EducatorPortal({
  user,
  onSignOut,
}: {
  user: User;
  onSignOut: () => Promise<void>;
}) {
  usePortalAppearance();
  const [home, setHome] = useState<HomeData | null>(null),
    [org, setOrg] = useState(''),
    [tab, setTab] = useState('Home');
  const [data, setData] = useState<Data | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const pendingKey = `marvin-educator-pending:${user.id}`;
  const [pending, setPending] = useState<Pending | null>(() => saved(pendingKey, null));
  const [knownRejected, setKnownRejected] = useState(false);
  const selected = useRef(org);
  selected.current = org;
  const [editor, setEditor] = useState<{
    kind: 'course' | 'assignment' | 'invite';
    record?: Course | Assignment;
  } | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  async function loadHome() {
    const value = await api<HomeData>('/educator');
    setHome(value);
    setOrg((current) =>
      value.organisations.some((o) => o.id === current)
        ? current
        : (pending?.org ?? value.organisations[0]?.id ?? ''),
    );
  }
  async function loadOrg(target = org) {
    if (!target) return;
    const value = await api<Data>(`/educator/${target}`);
    if (selected.current === target) {
      setData(value);
      setEditor((current) =>
        current?.record
          ? {
              ...current,
              record:
                current.kind === 'course'
                  ? value.courses.find((c) => c.id === current.record?.id)
                  : value.assignments.find((a) => a.id === current.record?.id),
            }
          : current,
      );
    }
  }
  useEffect(() => {
    let active = true;
    api<HomeData>('/educator')
      .then((value) => {
        if (active) {
          setHome(value);
          setOrg(pending?.org ?? value.organisations[0]?.id ?? '');
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [user.id]);
  useEffect(() => {
    setData(null);
    setReview(null);
    setEditor(null);
    setError('');
    if (org)
      void loadOrg(org).catch((e) => {
        if (selected.current === org) setError(e.message);
      });
  }, [org]);
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (pending || editor) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [pending, editor]);
  async function execute(body?: Command, target = org) {
    if (busy) return false;
    if (pending && body) {
      setError('Resolve the saved request before starting another change.');
      return false;
    }
    const request = pending ?? { org: target, body: { ...body!, requestId: crypto.randomUUID() } };
    try {
      remember(pendingKey, request);
    } catch {
      setError('Browser recovery storage is unavailable. No change was sent.');
      return false;
    }
    setPending(request);
    setBusy(true);
    setKnownRejected(false);
    setError('');
    try {
      await api(`/educator/${request.org}/actions`, 'POST', request.body);
      sessionStorage.removeItem(pendingKey);
      setPending(null);
      setEditor(null);
      // The refreshed row/form shows completion; reserve notices for attention.
      try {
        await loadHome();
        await loadOrg(request.org);
      } catch (e) {
        setError(`Saved, but the refreshed view is unavailable. ${(e as Error).message}`);
      }
      return true;
    } catch (e) {
      const failure = e as Error & { status?: number };
      setError(failure.message);
      setKnownRejected([400, 403, 404, 409, 413, 429].includes(failure.status ?? 0));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function openReview(id: string) {
    const target = org;
    setReview(null);
    setBusy(true);
    setError('');
    try {
      const value = await api<Review>(`/educator/${target}/reviews/${id}`);
      if (selected.current === target) setReview(value);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function navigate(value: string) {
    if (editor && !window.confirm('Leave this form? Its draft is retained in this browser tab.'))
      return;
    setEditor(null);
    setReview(null);
    setTab(value);
    setError('');
  }
  const admin = data?.membership.role === 'administrator',
    teacher = ['leader', 'educator'].includes(data?.membership.role ?? '');
  const available = data?.shares.filter((s) => s.active) ?? [];
  const waiting = available.filter(
    (s) => !s.feedback.some((f) => f.author === user.id && f.released),
  );
  return (
    <div className="educator-shell portal-design">
      <a className="skip-link" href="#educator-main">
        Skip to content
      </a>
      <aside className="educator-nav">
        <a className="educator-brand" href="/educator">
          <img src="/favicon.ico?v=marvin-head-v2" alt=""/><span>Marvin<small>Educator portal</small></span>
        </a>
        <nav aria-label="Educator navigation">
          {tabs
            .filter(([name]) => name !== 'Administration' || admin)
            .map(([name, Icon]) => (
              <button
                key={name}
                aria-current={tab === name ? 'page' : undefined}
                onClick={() => navigate(name)}
              >
                <Icon size={19} />
                {name}
              </button>
            ))}
        </nav>
        <div className="educator-nav-footer">
          <CitationCredit/>
          <div className="portal-educator-user"><span className="avatar">{user.initials}</span><span><strong>{user.name || user.email}</strong><small>Educator workspace</small></span></div>
          <a href="/account">Account and security</a>
          <a href="/home">Student home</a>
          <button
            disabled={busy || !!pending}
            onClick={() => {
              if (
                !editor ||
                window.confirm('Sign out? Your form draft will remain in this browser tab.')
              )
                void onSignOut().catch((e) => setError(e.message));
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <div className="educator-body">
        <header className="educator-top">
          <label>
            Institution
            <select
              aria-label="Institution"
              value={org}
              disabled={busy || !!pending || !!editor}
              onChange={(e) => setOrg(e.target.value)}
            >
              <option value="" disabled>
                Select institution
              </option>
              {home?.organisations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <span>{user.name || user.email}</span>
        </header>
        <main id="educator-main" tabIndex={-1}>
          <div className="educator-heading">
            <div>
              <p className="educator-kicker">{data?.organisation.name ?? 'YOUR TEACHING SPACE'}</p>
              <h1>{tab}</h1>
            </div>
            {data && <span className="educator-badge">{data.membership.role}</span>}
          </div>
          {error && (
            <ErrorNotice
              message={error}
              retry={() => {
                setError('');
                void (home ? loadOrg() : loadHome()).catch((e) => setError(e.message));
              }}
            />
          )}
          {pending && (
            <section className="educator-recovery" aria-label="Saved request">
              <strong>{busy ? 'Saving…' : 'A saved request needs a response'}</strong>
              <p>
                Your exact request is retained in this browser tab. Retry it before making another
                change.
              </p>
              <button disabled={busy} onClick={() => void execute()}>
                Retry saved request
              </button>
              {knownRejected && (
                <button
                  disabled={busy}
                  onClick={() => {
                    sessionStorage.removeItem(pendingKey);
                    setPending(null);
                    setKnownRejected(false);
                    setError('');
                  }}
                >
                  Dismiss rejected request; keep form draft
                </button>
              )}
            </section>
          )}
          {!home && !error && <Spinner label="Opening educator portal…" />}
          {home?.invitations.length ? (
            <section className="educator-panel">
              <h2>Invitations</h2>
              {home.invitations.map((i) => (
                <div className="educator-row" key={i.id}>
                  <div>
                    <strong>{i.orgName}</strong>
                    <p>
                      {i.role} · expires {date(i.expiresAt)}
                    </p>
                  </div>
                  {i.role === 'student' ? (
                    <span>Accept in the PC app</span>
                  ) : (
                    <button
                      disabled={busy || !!pending}
                      onClick={() => void execute({ action: 'invite.accept', id: i.id }, i.orgId)}
                    >
                      Accept invitation
                    </button>
                  )}
                </div>
              ))}
            </section>
          ) : null}
          {home && !home.organisations.length && (
            <section className="educator-empty">
              <GraduationCap size={32} />
              <h2>Your teaching space starts with an invitation</h2>
              <p>
                Ask your institution administrator to invite your verified Marvin email address.
                Choosing an institution in your student profile does not grant educator access.
              </p>
              <p>Institution not using Marvin yet? <a href="/educator/register-interest">Register interest</a>.</p>
            </section>
          )}
          {org && !data && !error && <Spinner label="Loading institution…" />}
          {data?.membership.role === 'student' ? (
            <section className="educator-panel">
              <h2>Student membership</h2>
              <p>
                Use the Marvin PC app for coursework and sharing. This workspace is for your
                institution’s educators.
              </p>
            </section>
          ) : (
            data && (
              <>
                {editor ? (
                  <EducatorForm
                    key={`${org}:${editor.kind}:${editor.record?.id ?? 'new'}`}
                    kind={editor.kind}
                    record={editor.record}
                    data={data}
                    userId={user.id}
                    disabled={busy || !!pending}
                    onSave={execute}
                    onClose={() => setEditor(null)}
                  />
                ) : review ? (
                  <ReviewPanel
                    key={review.share.id}
                    value={review}
                    userId={user.id}
                    disabled={busy || !!pending}
                    onBack={() => setReview(null)}
                    onSave={async (body) => {
                      if (await execute(body)) {
                        sessionStorage.removeItem(
                          `marvin-educator-feedback:${user.id}:${review.share.id}`,
                        );
                        await openReview(review.share.id);
                      }
                    }}
                  />
                ) : (
                  <>
                    {tab === 'Home' && (
                      <>
                        <p className="educator-intro">
                          Set clear expectations, review shared work and help students take their
                          next step.
                        </p>
                        <div className="educator-metrics">
                          <button onClick={() => navigate('Courses')}>
                            <strong>{data.courses.filter((c) => !c.archived).length}</strong>Active
                            courses
                          </button>
                          <button onClick={() => navigate('Reviews')}>
                            <strong>{waiting.length}</strong>Awaiting your feedback
                          </button>
                          <button onClick={() => navigate('Assignments')}>
                            <strong>
                              {
                                data.assignments.filter(
                                  (a) => a.published && !a.published.withdrawn,
                                ).length
                              }
                            </strong>
                            Published assignments
                          </button>
                        </div>
                        <section className="educator-panel">
                          <h2>Next to review</h2>
                          {waiting.length ? (
                            waiting
                              .slice(0, 8)
                              .map((s) => (
                                <ShareRow
                                  key={s.id}
                                  share={s}
                                  open={() => void openReview(s.id)}
                                  disabled={busy}
                                />
                              ))
                          ) : (
                            <p>
                              {admin
                                ? 'Coursework is visible only to its named academic reviewers. Administrative access does not expose student work.'
                                : 'No shared work is waiting for your feedback.'}
                            </p>
                          )}
                        </section>
                        <p className="educator-muted">
                          Students choose which saved work to share. Your portal does not show their
                          live drafts or private study.
                        </p>
                      </>
                    )}
                    {tab === 'Courses' && (
                      <section className="educator-panel">
                        <div className="educator-section-heading">
                          <h2>Your courses</h2>
                          {admin && (
                            <button
                              disabled={busy || !!pending}
                              onClick={() => setEditor({ kind: 'course' })}
                            >
                              Add course
                            </button>
                          )}
                        </div>
                        {data.courses.length ? (
                          data.courses.map((c) => (
                            <div className="educator-row" key={c.id}>
                              <div>
                                <strong>{c.title}</strong>
                                <p>
                                  {[c.module, c.year, c.archived ? 'Archived' : 'Active']
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                              </div>
                              {admin && (
                                <button
                                  disabled={busy || !!pending}
                                  onClick={() => setEditor({ kind: 'course', record: c })}
                                >
                                  Edit course
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <p>No courses have been assigned yet.</p>
                        )}
                      </section>
                    )}
                    {tab === 'Assignments' && (
                      <section className="educator-panel">
                        <div className="educator-section-heading">
                          <h2>Assignment briefs</h2>
                          {teacher && (
                            <button
                              disabled={busy || !!pending || !data.courses.some((c) => !c.archived)}
                              onClick={() => setEditor({ kind: 'assignment' })}
                            >
                              Create assignment
                            </button>
                          )}
                        </div>
                        {data.assignments.length ? (
                          data.assignments.map((a) => (
                            <div className="educator-row" key={a.id}>
                              <div>
                                <strong>{a.draft.title}</strong>
                                <p>
                                  {data.courses.find((c) => c.id === a.courseId)?.title} ·{' '}
                                  {a.published
                                    ? a.published.withdrawn
                                      ? 'Withdrawn'
                                      : `Published version ${a.published.number}`
                                    : 'Draft'}{' '}
                                  · due {date(a.draft.dueAt)}
                                </p>
                              </div>
                              <button
                                disabled={busy || !!pending}
                                onClick={() => setEditor({ kind: 'assignment', record: a })}
                              >
                                {teacher ? 'Open brief' : 'View brief'}
                              </button>
                            </div>
                          ))
                        ) : (
                          <p>
                            {admin
                              ? 'Briefs are managed by course educators. Invite teaching staff through Administration.'
                              : 'No assignment briefs yet.'}
                          </p>
                        )}
                      </section>
                    )}
                    {tab === 'Reviews' && (
                      <section className="educator-panel">
                        <h2>Work shared with you</h2>
                        <p className="educator-muted">
                          A fixed version and selected evidence. Sharing is separate from university
                          submission.
                        </p>
                        {data.shares.length ? (
                          data.shares.map((s) => (
                            <ShareRow
                              key={s.id}
                              share={s}
                              open={() => void openReview(s.id)}
                              disabled={busy}
                            />
                          ))
                        ) : (
                          <p>No student work has been shared with you.</p>
                        )}
                      </section>
                    )}
                    {tab === 'Policies' && (
                      <PolicyPanel institutionId={data.organisation.institutionId} />
                    )}
                    {tab === 'Administration' && admin && (
                      <>
                        <section className="educator-panel">
                          <div className="educator-section-heading">
                            <h2>People and access</h2>
                            <button
                              disabled={busy || !!pending}
                              onClick={() => setEditor({ kind: 'invite' })}
                            >
                              Invite a member
                            </button>
                          </div>
                          {data.members.map((m) => (
                            <div className="educator-row" key={m.id}>
                              <div>
                                <strong>{m.name ?? 'Institution administrator'}</strong>
                                <p>
                                  {m.role} · {m.active ? 'Active' : 'Access removed'} ·{' '}
                                  {m.courseIds
                                    .map(
                                      (id) =>
                                        data.courses.find((c) => c.id === id)?.title ??
                                        'Archived course',
                                    )
                                    .join(', ')}
                                </p>
                              </div>
                              {m.active && (
                                <button
                                  disabled={busy || !!pending}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        'Remove this member’s institution access? Their existing work will be preserved.',
                                      )
                                    )
                                      void execute({
                                        action: 'member.revoke',
                                        id: m.id,
                                        revision: m.revision,
                                      });
                                  }}
                                >
                                  Remove access
                                </button>
                              )}
                            </div>
                          ))}
                        </section>
                        <section className="educator-panel">
                          <h2>Invitations</h2>
                          {data.invitations.length ? (
                            data.invitations.map((i) => (
                              <div className="educator-row" key={i.id}>
                                <div>
                                  <strong>{i.email}</strong>
                                  <p>
                                    {i.role} · {i.status} · expires {date(i.expiresAt)}
                                  </p>
                                </div>
                                {i.status === 'pending' && (
                                  <button
                                    disabled={busy || !!pending}
                                    onClick={() =>
                                      void execute({
                                        action: 'invite.revoke',
                                        id: i.id,
                                        revision: i.revision,
                                      })
                                    }
                                  >
                                    Revoke invitation
                                  </button>
                                )}
                              </div>
                            ))
                          ) : (
                            <p>No invitations yet.</p>
                          )}
                          <p className="educator-muted">
                            Invitations appear when the recipient signs in with their verified
                            email. This version does not send invitation emails.
                          </p>
                        </section>
                        <section className="educator-panel">
                          <h2>Recent access and changes</h2>
                          {data.audit.map((a, i) => (
                            <p key={i}>
                              {date(a.at)} · {a.action} ·{' '}
                              {data.members.find((m) => m.id === a.actor)?.name ??
                                (a.actor === 'operator' ? 'Verified setup' : 'Institution member')}
                            </p>
                          ))}
                        </section>
                      </>
                    )}
                  </>
                )}
              </>
            )
          )}
        </main>
      </div>
    </div>
  );
}

function ShareRow({
  share,
  open,
  disabled,
}: {
  share: Share;
  open: () => void;
  disabled: boolean;
}) {
  return (
    <div className="educator-row">
      <div>
        <strong>{share.title}</strong>
        <p>
          Saved version {share.version} · shared {date(share.capturedAt)} ·{' '}
          {share.active ? 'Available' : 'Expired or revoked'}
        </p>
      </div>
      <button disabled={disabled || !share.active} onClick={open}>
        Review work
      </button>
    </div>
  );
}

function EducatorForm({
  kind,
  record,
  data,
  userId,
  disabled,
  onSave,
  onClose,
}: {
  kind: 'course' | 'assignment' | 'invite';
  record?: Course | Assignment;
  data: Data;
  userId: string;
  disabled: boolean;
  onSave: (body: Command) => Promise<boolean>;
  onClose: () => void;
}) {
  const key = `marvin-educator-draft:${userId}:${data.organisation.id}:${kind}:${record?.id ?? 'new'}`;
  const course = record as Course | undefined,
    brief = (record as Assignment | undefined)?.draft;
  const baseline: Record<string, string | boolean | string[]> =
    kind === 'course'
      ? {
          title: course?.title ?? '',
          module: course?.module ?? '',
          year: course?.year ?? '',
          archived: course?.archived ?? false,
        }
      : kind === 'invite'
        ? { email: '', role: 'educator', courseIds: [] }
        : brief
          ? { ...brief }
          : {
              courseId: data.courses.find((c) => !c.archived)?.id ?? '',
              title: '',
              instructions: '',
              outcomes: '',
              deliverables: 'Main document',
              referencing: '',
              assistance: '',
              dueAt: '',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
  const [restored] = useState(() =>
    saved(key, { fields: baseline, revision: record?.revision ?? 0 }),
  );
  const [baseRevision, setBaseRevision] = useState(restored.revision);
  const [fields, setFields] = useState(() => restored.fields),
    [storageError, setStorageError] = useState(''),
    [preview, setPreview] = useState(false);
  const isTeacher = ['leader', 'educator'].includes(data.membership.role),
    readOnly = kind === 'assignment' && !isTeacher;
  function change(name: string, value: string | boolean | string[]) {
    const next = { ...fields, [name]: value };
    setFields(next);
    setPreview(false);
    try {
      remember(key, { fields: next, revision: baseRevision });
      setStorageError('');
    } catch {
      setStorageError('This form is held in memory only. Browser recovery storage is unavailable.');
    }
  }
  function field(name: string, label: string, multi = false, required = false, maxLength = 4000) {
    return (
      <label key={name}>
        {label}
        {multi ? (
          <textarea
            aria-label={label}
            value={String(fields[name] ?? '')}
            onChange={(e) => change(name, e.target.value)}
            required={required}
            maxLength={maxLength}
            rows={name === 'instructions' ? 7 : 3}
          />
        ) : (
          <input
            value={String(fields[name] ?? '')}
            onChange={(e) => change(name, e.target.value)}
            required={required}
            maxLength={maxLength}
          />
        )}
      </label>
    );
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (disabled || readOnly) return;
    const body: Command =
      kind === 'invite'
        ? {
            action: 'member.invite',
            ...fields,
            expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
          }
        : { action: `${kind}.save`, ...fields, id: record?.id, revision: baseRevision };
    // Course drafts never send server-managed fields back through the schema.
    if (kind === 'course') {
      delete body.published;
      delete body.publications;
    }
    if (await onSave(body)) sessionStorage.removeItem(key);
  }
  const assignment = record as Assignment | undefined;
  return (
    <section className="educator-panel">
      <div className="educator-section-heading">
        <h2>
          {kind === 'invite'
            ? 'Invite a member'
            : kind === 'course'
              ? 'Course details'
              : 'Assignment brief'}
        </h2>
        <button disabled={disabled} onClick={onClose}>
          Back
        </button>
      </div>
      {storageError && <p role="alert">{storageError}</p>}
      {record && baseRevision !== record.revision && (
        <section className="educator-recovery">
          <h3>A newer saved version exists</h3>
          <p>
            Your draft is retained below. Review the current saved fields before applying your draft
            to this version.
          </p>
          <details>
            <summary>Current saved fields</summary>
            {Object.entries(baseline).map(([name, value]) => (
              <p key={name}>
                <strong>{name === 'courseId' ? 'Course' : name}:</strong>{' '}
                {name === 'courseId'
                  ? data.courses.find((c) => c.id === value)?.title
                  : String(value)}
              </p>
            ))}
          </details>
          <button
            disabled={disabled || readOnly}
            onClick={() => {
              setBaseRevision(record.revision);
              try {
                remember(key, { fields, revision: record.revision });
              } catch {
                setStorageError('Your draft remains in memory. Keep this tab open.');
              }
            }}
          >
            Use current version as the save basis
          </button>
        </section>
      )}
      <form className="educator-form" onSubmit={(e) => void submit(e)}>
        <fieldset disabled={disabled || readOnly}>
          {kind === 'course' ? (
            <>
              {field('title', 'Course title', false, true, 160)}
              {field('module', 'Module (optional)', false, false, 120)}
              {field('year', 'Academic year', false, true, 60)}
              <label className="educator-check">
                <input
                  type="checkbox"
                  checked={!!fields.archived}
                  onChange={(e) => change('archived', e.target.checked)}
                />
                Archive course
              </label>
            </>
          ) : kind === 'invite' ? (
            <>
              <label>
                Email address
                <input
                  type="email"
                  required
                  maxLength={254}
                  value={String(fields.email)}
                  onChange={(e) => change('email', e.target.value)}
                />
              </label>
              <label>
                Role
                <select
                  value={String(fields.role)}
                  onChange={(e) => change('role', e.target.value)}
                >
                  {['educator', 'leader', 'reviewer', 'administrator', 'student'].map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </label>
              <fieldset>
                <legend>Course access</legend>
                {data.courses
                  .filter((c) => !c.archived)
                  .map((c) => (
                    <label className="educator-check" key={c.id}>
                      <input
                        type="checkbox"
                        checked={(fields.courseIds as string[]).includes(c.id)}
                        onChange={(e) =>
                          change(
                            'courseIds',
                            e.target.checked
                              ? [...(fields.courseIds as string[]), c.id]
                              : (fields.courseIds as string[]).filter((id) => id !== c.id),
                          )
                        }
                      />
                      {c.title} · {c.year}
                    </label>
                  ))}
              </fieldset>
              <p>
                Expires in seven days. The recipient must verify this exact email address before
                accepting.
              </p>
            </>
          ) : (
            <>
              <label>
                Course
                <select
                  required
                  disabled={!!record}
                  value={String(fields.courseId)}
                  onChange={(e) => change('courseId', e.target.value)}
                >
                  {data.courses
                    .filter((c) => !c.archived || c.id === fields.courseId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title} · {c.year}
                      </option>
                    ))}
                </select>
              </label>
              {field('title', 'Assignment title', false, true, 160)}
              {field('instructions', 'Instructions', true, true, 12000)}
              {field('outcomes', 'Learning outcomes', true)}
              {field('deliverables', 'Required deliverables', true, true, 2000)}
              <label>
                Deadline (UTC)
                <input
                  type="datetime-local"
                  required
                  value={String(fields.dueAt).slice(0, 16)}
                  onChange={(e) =>
                    change(
                      'dueAt',
                      e.target.value ? new Date(`${e.target.value}:00Z`).toISOString() : '',
                    )
                  }
                />
              </label>
              {field('timezone', 'Display time zone', false, true, 80)}
              {field('referencing', 'Referencing instructions', true, false, 2000)}
              {field('assistance', 'Assistance guidance', true)}
              <p className="educator-muted">
                Guidance is recorded with this brief. It does not activate AI permissions; existing
                assessment restrictions continue to apply.
              </p>
            </>
          )}
          {!readOnly && (
            <div className="educator-actions">
              <button type="submit">
                {kind === 'invite' ? 'Create invitation' : 'Save draft'}
              </button>
              {kind === 'assignment' && (
                <button type="button" onClick={() => setPreview(!preview)}>
                  Preview instructions
                </button>
              )}
            </div>
          )}
        </fieldset>
      </form>
      {preview && (
        <article className="educator-preview">
          <h3>{String(fields.title)}</h3>
          <p>{data.courses.find((c) => c.id === fields.courseId)?.title}</p>
          {['instructions', 'outcomes', 'deliverables', 'referencing', 'assistance'].map((k) => (
            <p key={k}>{String(fields[k])}</p>
          ))}
          <p>
            Due {fields.dueAt ? date(String(fields.dueAt)) : 'No deadline'} · saved time zone{' '}
            {String(fields.timezone)}
          </p>
        </article>
      )}
      {kind === 'assignment' && assignment && (
        <section className="educator-publications">
          <h3>Publication</h3>
          <p>
            Publishing uses the last saved draft. Students retain the instructions attached to their
            existing work.
          </p>
          {isTeacher && (
            <div className="educator-actions">
              <button
                disabled={disabled}
                onClick={() => {
                  if (
                    window.confirm(
                      'Publish the saved draft to this course? Unsaved form changes are not included.',
                    )
                  )
                    void onSave({
                      action: 'assignment.publish',
                      id: assignment.id,
                      revision: assignment.revision,
                    });
                }}
              >
                Publish saved draft
              </button>
              {assignment.published && !assignment.published.withdrawn && (
                <button
                  disabled={disabled}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Withdraw this assignment from new acceptance? Existing records remain.',
                      )
                    )
                      void onSave({
                        action: 'assignment.withdraw',
                        id: assignment.id,
                        revision: assignment.revision,
                      });
                  }}
                >
                  Withdraw
                </button>
              )}
            </div>
          )}
          <details>
            <summary>Publication history ({assignment.publications.length})</summary>
            {assignment.publications.map((p) => (
              <article key={p.number}>
                <h4>
                  Version {p.number} · {p.withdrawn ? 'Withdrawn' : 'Published'} · {date(p.at)}
                </h4>
                <p>{p.instructions}</p>
                <p>{p.assistance}</p>
              </article>
            ))}
          </details>
        </section>
      )}
    </section>
  );
}

function ReviewPanel({
  value,
  userId,
  disabled,
  onBack,
  onSave,
}: {
  value: Review;
  userId: string;
  disabled: boolean;
  onBack: () => void;
  onSave: (body: Command) => Promise<void>;
}) {
  const current = value.feedback.find((f) => f.author === userId),
    key = `marvin-educator-feedback:${userId}:${value.share.id}`;
  const [restored] = useState(() =>
    saved(key, { text: current?.text ?? '', revision: current?.revision ?? 0 }),
  );
  const [baseRevision, setBaseRevision] = useState(restored.revision);
  const [text, setText] = useState(() => restored.text),
    [error, setError] = useState('');
  return (
    <section className="educator-panel">
      <div className="educator-section-heading">
        <div>
          <h2>{value.snapshot.title}</h2>
          <p>
            Saved version {value.snapshot.version} · shared {date(value.share.capturedAt)}
          </p>
        </div>
        <button disabled={disabled} onClick={onBack}>
          Back to reviews
        </button>
      </div>
      <p className="educator-muted">{value.snapshot.limitations}</p>
      {value.snapshot.files.map((f) => (
        <article className="educator-reading" key={f.id}>
          <h3>
            {f.name} · version {f.version}
          </h3>
          <pre>{f.text || 'No text in this file.'}</pre>
        </article>
      ))}
      {!!value.snapshot.sources.length && (
        <section>
          <h3>Selected references</h3>
          {value.snapshot.sources.map((s, i) => (
            <p key={i}>
              {s.url && /^https?:\/\//.test(s.url) ? (
                <a href={s.url} target="_blank" rel="noopener noreferrer">
                  {s.title || s.url}
                </a>
              ) : (
                s.title
              )}
            </p>
          ))}
        </section>
      )}
      <form className="educator-form" onSubmit={(e) => e.preventDefault()}>
        <h3>Your feedback</h3>
        <label>
          Feedback on this saved version
          <textarea
            aria-label="Feedback on this saved version"
            value={text}
            maxLength={12000}
            rows={6}
            disabled={disabled}
            onChange={(e) => {
              setText(e.target.value);
              try {
                remember(key, { text: e.target.value, revision: baseRevision });
                setError('');
              } catch {
                setError('Feedback is held in memory only. Keep this tab open.');
              }
            }}
          />
        </label>
        {error && <p role="alert">{error}</p>}
        {baseRevision !== (current?.revision ?? 0) && (
          <section className="educator-recovery">
            <h3>Feedback has changed since this draft</h3>
            <p>Current saved feedback: {current?.text}</p>
            <button
              disabled={disabled}
              onClick={() => {
                const revision = current?.revision ?? 0;
                setBaseRevision(revision);
                try {
                  remember(key, { text, revision });
                } catch {
                  setError('Your draft remains in memory. Keep this tab open.');
                }
              }}
            >
              Use current feedback as the save basis
            </button>
          </section>
        )}
        <p>
          {current?.released
            ? 'Your previous feedback is released. Corrections retain its history.'
            : 'Draft feedback is private until you release it.'}
        </p>
        <div className="educator-actions">
          {!current?.released && (
            <button
              disabled={disabled || !text.trim()}
              onClick={() =>
                void onSave({
                  action: 'feedback.save',
                  id: value.share.id,
                  revision: baseRevision,
                  text,
                  release: false,
                })
              }
            >
              Save private draft
            </button>
          )}
          <button
            disabled={disabled || !text.trim()}
            onClick={() => {
              if (window.confirm('Release this feedback to the student?'))
                void onSave({
                  action: 'feedback.save',
                  id: value.share.id,
                  revision: baseRevision,
                  text,
                  release: true,
                });
            }}
          >
            Release feedback
          </button>
        </div>
      </form>
    </section>
  );
}

function PolicyPanel({ institutionId }: { institutionId: string }) {
  type References = {
    sources: {
      id: string;
      title: string;
      url: string;
      status: string;
      lastSuccessAt: string | null;
      review: { summary: string; reviewedAt: string; effectiveAt: string } | null;
    }[];
  };
  const [value, setValue] = useState<References | null>(null),
    [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setValue(null);
    setError('');
    api<References>(`/institutions/${institutionId}`)
      .then((v) => {
        if (active) setValue(v);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [institutionId]);
  return (
    <section className="educator-panel">
      <h2>Institution references</h2>
      <p>
        Public guidance is maintained and reviewed through Marvin operations. A new source copy does
        not change assessment permissions.
      </p>
      {error ? (
        <ErrorNotice message={error} />
      ) : value ? (
        value.sources.map((s) => (
          <article className="educator-reading" key={s.id}>
            <h3>{s.title}</h3>
            <p>
              Status: {s.status.replaceAll('-', ' ')} ·{' '}
              {s.lastSuccessAt
                ? `Last retrieved ${date(s.lastSuccessAt)}`
                : 'No successful retrieval recorded'}
            </p>
            {s.review ? (
              <>
                <p>{s.review.summary}</p>
                <p className="educator-muted">
                  Reviewed {date(s.review.reviewedAt)} · effective {s.review.effectiveAt}
                </p>
              </>
            ) : (
              <p>No reviewed summary is available.</p>
            )}
            {/^https:\/\//.test(s.url) && (
              <a href={s.url} target="_blank" rel="noopener noreferrer">
                Open official source
              </a>
            )}
          </article>
        ))
      ) : (
        <Spinner label="Loading reference…" />
      )}
    </section>
  );
}
